/**
 * Spec Phase 4 Slice 4D — SectionNav.
 * BUG-033 — drafter exclude/reorder controls.
 *
 * 244px left rail. Renders 12 SectionRows (in catalogue order)
 * + a CompletenessBar pinned to the bottom.
 *
 * Selection updates Zustand store (setActiveSection); paragraphs
 * lazy-load via store.loadActiveSectionParagraphs.
 *
 * Exclude/reorder (BUG-033):
 *   - Each row carries an ✕ icon that toggles is_excluded via
 *     PATCH /sections/{id}/visibility. Auto-generated sections
 *     (claims_mirror, reference_signs_list) have no toggle.
 *   - HTML5 drag-and-drop reorders sections; on drop, we send a
 *     batch-style PATCH for every section with a new index so the
 *     server-side display_order matches the rail. After the PATCH
 *     resolves the store's sections list updates (the response
 *     is the full re-ordered payload).
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import { patchSpecSectionVisibility, patchSpecSectionRename } from '@/api/client';
import SectionRow from './SectionRow';
import CompletenessBar from './CompletenessBar';

export default function SectionNav(): JSX.Element {
  const sections = useSpecStore((s) => s.sections);
  const activeSectionId = useSpecStore((s) => s.activeSectionId);
  // BUG-SPEC-005 Phase 4a — compute completeness live from the
  // section_summary instead of reading specDocument.overall_-
  // completeness_pct (which is materialised at section-generate
  // time but routinely stale; brief observed `0` while
  // section_summary reported 6/12 drafted). The -2 mirrors
  // SpecHeader.tsx:194's auto-section discount (reference_signs_list
  // + claims_mirror are non-editable and excluded from the
  // completeness denominator).
  const sectionSummary = useSpecStore((s) => s.sectionSummary);
  const _liveCompletenessPct = (() => {
    const total = sectionSummary?.total ?? 0;
    const drafted = sectionSummary?.drafted ?? 0;
    const editableTotal = Math.max(0, total - 2);
    if (editableTotal <= 0) return 0;
    return Math.round((drafted / editableTotal) * 100);
  })();
  const setActiveSection = useSpecStore((s) => s.setActiveSection);
  const matterId = useSpecStore((s) => s.matterId);
  const setSections = useSpecStore((s) => s.setSections);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);

  const onToggleExcluded = useCallback(
    async (sectionId: string, nextExcluded: boolean): Promise<void> => {
      if (!matterId) return;
      try {
        const r = await patchSpecSectionVisibility(matterId, sectionId, {
          is_excluded: nextExcluded,
        });
        if (r?.sections) setSections(r.sections);
      } catch (e: unknown) {
        // Surface via the store's mutation-error channel (renders
        // the corner banner). Same pattern as patchSpecSection
        // elsewhere in the codebase.
        console.warn('[SectionNav] toggle excluded failed', e);
      }
    },
    [matterId, setSections],
  );

  // BUG-048 — drafter-rename PATCH. Returns a boolean so the
  // SectionRow can flash "saved" on success or surface an
  // error inline on failure. Auto-generated sections never
  // call into this path; the SectionNav passes ``null`` for
  // their onRename prop.
  const onRenameSection = useCallback(
    async (sectionId: string, nextTitle: string | null): Promise<boolean> => {
      if (!matterId) return false;
      try {
        const r = await patchSpecSectionRename(matterId, sectionId, nextTitle);
        if (r?.sections) setSections(r.sections);
        return true;
      } catch (e: unknown) {
        console.warn('[SectionNav] rename failed', e);
        return false;
      }
    },
    [matterId, setSections],
  );

  const onDragStart = useCallback(
    (id: string, e: React.DragEvent<HTMLDivElement>) => {
      setDraggingId(id);
      // Required for Firefox drag to fire.
      e.dataTransfer.setData('text/plain', id);
      e.dataTransfer.effectAllowed = 'move';
    }, [],
  );
  const onDragOver = useCallback(
    (id: string, e: React.DragEvent<HTMLDivElement>) => {
      if (!draggingId || draggingId === id) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setHoverId(id);
    }, [draggingId],
  );
  const onDragEnd = useCallback(() => {
    setDraggingId(null);
    setHoverId(null);
  }, []);
  const onDrop = useCallback(
    async (targetId: string, e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const sourceId = draggingId;
      setDraggingId(null);
      setHoverId(null);
      if (!matterId || !sourceId || sourceId === targetId) return;
      const cur = [...sections];
      const fromIdx = cur.findIndex((s) => s.id === sourceId);
      const toIdx = cur.findIndex((s) => s.id === targetId);
      if (fromIdx < 0 || toIdx < 0) return;
      const [moved] = cur.splice(fromIdx, 1);
      cur.splice(toIdx, 0, moved);
      // Optimistic local reorder so the rail snaps immediately.
      setSections(cur.map((s, i) => ({ ...s, display_order: i + 1 })));
      // Persist each affected section's display_order. We send
      // every row in the new order — cheap, idempotent, and
      // guarantees the server-side ordering matches even if the
      // drag crossed a section that hadn't moved before.
      try {
        let last: { sections?: typeof cur } | null = null;
        for (let i = 0; i < cur.length; i += 1) {
          last = await patchSpecSectionVisibility(matterId, cur[i].id, {
            display_order: i + 1,
          });
        }
        if (last?.sections) setSections(last.sections);
      } catch (err: unknown) {
        console.warn('[SectionNav] reorder PATCH failed', err);
      }
    },
    [draggingId, matterId, sections, setSections],
  );

  // BUG-045 — keep the active section visible in the rail when the
  // observer-driven scroll moves the highlight. ``nearest`` block
  // alignment is a no-op when the row is already in view, so we
  // don't fight against the user's manual nav scroll.
  const railRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!activeSectionId) return;
    const el = railRef.current?.querySelector<HTMLElement>(
      `[data-section-id="${activeSectionId}"]`,
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeSectionId]);

  const toggleLeftRail = useSpecStore((s) => s.toggleLeftRail);

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid="spec-section-nav"
    >
      {/* BUG-040 — collapse chevron pinned to the rail header so
          the drafter can hide the panel and let the centre paper
          area expand. The expand chevron lives on the collapsed
          strip (SpecBody.CollapsedLeftStrip). */}
      <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-1.5">
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
          Sections
        </span>
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
          onClick={toggleLeftRail}
          aria-label="Collapse section panel"
          title="Collapse to the section-number strip"
          data-testid="spec-left-rail-collapse"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <div ref={railRef} className="flex-1 min-h-0 overflow-y-auto py-2">
        {sections.length === 0 ? (
          <div className="px-3 py-4 text-xs text-zinc-400">
            No sections. Seed has not run yet.
          </div>
        ) : (
          sections.map((section) => (
            <SectionRow
              key={section.id}
              section={section}
              isActive={section.id === activeSectionId}
              onClick={() => {
                setActiveSection(section.id);
                // BUG-045 — request a smooth scroll to the matching
                // section block in the centre pane. PaperEditor
                // listens for this event; observer-driven activeSectionId
                // changes (scroll → setActiveSection) don't fire it,
                // so the click-to-scroll path is loop-free.
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent(
                    'spec.scroll-to-section',
                    { detail: { id: section.id } },
                  ));
                }
              }}
              onToggleExcluded={section.is_auto_generated
                ? null
                : (next) => { void onToggleExcluded(section.id, next); }}
              onRename={section.is_auto_generated
                ? null
                : (next) => onRenameSection(section.id, next)}
              draggable
              onDragStart={(e) => onDragStart(section.id, e)}
              onDragOver={(e) => onDragOver(section.id, e)}
              onDrop={(e) => { void onDrop(section.id, e); }}
              onDragEnd={onDragEnd}
              isDropTarget={hoverId === section.id}
            />
          ))
        )}
      </div>
      <CompletenessBar
        pct={_liveCompletenessPct}
      />
    </div>
  );
}
